// src/shared/dsp/cardTokens.ts
// ────────────────────────────────────────────────────────────────────────────
// PAC Glass Card Design Tokens — SINGLE SOURCE OF TRUTH
//
// Every chat-card component across every domain imports from HERE.
// To white-label for a business customer: swap accent colors in one place.
// To tweak spacing/radii/opacity: change one token, all 27+ cards update.
//
// Convention: tokens are plain class strings composed at render time.
// Card components use `${CARD.container}` not ad-hoc inline classes.
// ────────────────────────────────────────────────────────────────────────────

// ── Card Layout ──────────────────────────────────────────────────────────

export const CARD = {
  /** Standard card container — transparent with border contour. */
  container:
    'bg-transparent rounded-lg overflow-hidden border border-white/[0.08]',

  /** Subtle card (IAM wrappers, thin wrappers). */
  containerSubtle:
    'bg-transparent rounded-lg overflow-hidden border border-white/[0.06]',

  /** Loading placeholder card. */
  loading:
    'bg-transparent rounded-lg border border-white/[0.08] p-3 animate-pulse',

  /** Error state card. */
  error:
    'bg-transparent rounded-lg border border-red-500/30 p-3',

  /** Empty state wrapper. */
  empty:
    'bg-transparent rounded-lg px-3 py-3 text-xs text-neutral-600 italic border border-white/[0.08]',

  // ── Header ──

  /** Standard card header bar. */
  header:
    'bg-transparent flex items-center gap-2 px-3 py-1.5 border-b border-white/[0.06]',

  /** Header with space-between (title left, controls right). */
  headerSpaced:
    'bg-transparent flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]',

  // ── Sections ──

  /** Standard content section. */
  section:     'px-3 py-2',
  /** Compact section (single-row metadata). */
  sectionSm:   'px-3 py-1.5',
  /** Section with top divider (footer, inline-text area). */
  sectionDiv:  'px-3 py-2 border-t border-white/[0.04]',
  /** Spacious section (metric grids). */
  sectionLg:   'px-3 py-3',

  // ── Dividers ──

  /** Standard horizontal divider. */
  divider:       'border-t border-white/[0.04]',
  /** Bottom divider (mid-card breaks). */
  dividerBottom: 'border-b border-white/[0.04]',
  /** Row list divider (standard density). */
  rowList:       'divide-y divide-white/[0.04]',
  /** Dense row list (scope-key drill-down). */
  rowListDense:  'divide-y divide-white/[0.03]',

  // ── Row interaction ──

  /** Hover state for list rows. */
  rowHover: 'hover:bg-white/[0.02] transition-colors',
} as const;

// ── Badges ───────────────────────────────────────────────────────────────

export const BADGE = {
  /** Badge wrapper (always applied alongside a color class). */
  base: 'px-1.5 py-0.5 rounded text-[10px] font-mono uppercase',

  // ── Semantic status colors ──
  success:  'bg-emerald-600/20 text-emerald-400',
  warning:  'bg-amber-600/20 text-amber-400',
  error:    'bg-red-600/20 text-red-400',
  info:     'bg-sky-600/20 text-sky-400',
  neutral:  'bg-white/[0.06] text-neutral-400',

  // ── Domain accent colors (for domain-label badges) ──
  violet:   'bg-violet-600/20 text-violet-400',
  cyan:     'bg-cyan-600/20 text-cyan-400',
  purple:   'bg-purple-600/20 text-purple-400',
  indigo:   'bg-indigo-600/20 text-indigo-400',
  orange:   'bg-orange-600/20 text-orange-400',
  teal:     'bg-teal-600/20 text-teal-400',
  fuchsia:  'bg-fuchsia-600/15 text-fuchsia-400',
  blue:     'bg-blue-600/20 text-blue-400',
  emerald:  'bg-emerald-600/20 text-emerald-400',
} as const;

/** Compose a badge: `className={badge('success')}` or `className={badge('violet')}` */
export function badge(color: keyof typeof BADGE): string {
  if (color === 'base') return BADGE.base;
  return `${BADGE.base} ${BADGE[color]}`;
}

// ── Status Map ───────────────────────────────────────────────────────────
// Reusable status→badge class map. Cards import this instead of declaring
// their own per-file status objects.

export const STATUS_CLASSES: Record<string, string> = {
  // Success group
  completed:            BADGE.success,
  succeeded:            BADGE.success,
  applied:              BADGE.success,
  sent:                 BADGE.success,
  active:               BADGE.success,
  complete:             BADGE.success,
  confirmed:            BADGE.success,

  // Warning group
  processing:           BADGE.warning,
  running:              BADGE.warning,
  pending_confirmation: BADGE.warning,
  awaiting_confirm:     BADGE.warning,
  capturing:            BADGE.info,
  diffing:              BADGE.indigo,
  previewing:           BADGE.warning,
  applying:             BADGE.violet,

  // Error group
  failed:               BADGE.error,
  rejected:             BADGE.error,
  error:                BADGE.error,
  overdue:              BADGE.error,

  // Neutral group
  pending:              BADGE.neutral,
  unknown:              BADGE.neutral,
  skipped:              BADGE.neutral,
  synced:               BADGE.neutral,
  draft:                BADGE.neutral,
};

/** Get badge color for a status string, with neutral fallback. */
export function statusBadge(status: string): string {
  return `${BADGE.base} ${STATUS_CLASSES[status] ?? BADGE.neutral}`;
}

// ── Buttons ──────────────────────────────────────────────────────────────
// All buttons use pac-glass-btn (border-hover emphasis, NOT bg swap).

export const BTN = {
  /** Confirm / Apply / Launch (emerald accent). */
  primary:
    'pac-glass-btn rounded px-3 py-1 text-xs font-medium text-emerald-400 hover:border-emerald-500/50 disabled:opacity-50 transition-colors',

  /** Cancel / Dismiss (neutral). */
  secondary:
    'pac-glass-btn rounded px-3 py-1 text-xs font-medium text-neutral-400 hover:border-neutral-500/50 disabled:opacity-50 transition-colors',

  /** Destructive / Reject (red accent). */
  danger:
    'pac-glass-btn rounded px-3 py-1 text-xs font-medium text-red-400 hover:border-red-500/50 disabled:opacity-50 transition-colors',

  /** Full-width pair (used in two-button confirm/reject layouts). */
  wide: {
    primary:
      'pac-glass-btn flex-1 py-1.5 text-sm text-emerald-400 border-emerald-500/30 hover:border-emerald-500/60 disabled:opacity-50 transition-colors',
    secondary:
      'pac-glass-btn flex-1 py-1.5 text-sm text-red-400 border-red-500/30 hover:border-red-500/60 disabled:opacity-50 transition-colors',
  },

  /** Small inline action (copy button, code expand). */
  inline:
    'pac-glass-btn rounded px-2 py-0.5 text-[10px] text-neutral-400 hover:border-neutral-500/50 disabled:opacity-50',
} as const;

// ── Progress bars ────────────────────────────────────────────────────────

export const PROGRESS = {
  /** Progress bar track (background). */
  track:   'h-1.5 rounded-full bg-white/[0.04] overflow-hidden',

  /** Progress bar fill — compose with a width style. */
  fill:    'h-full rounded-full transition-all duration-500',

  /** Fill color accents. */
  color: {
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error:   'bg-red-500',
    info:    'bg-blue-500',
    sky:     'bg-sky-500',
    indigo:  'bg-indigo-500',
    violet:  'bg-violet-500',
  },
} as const;

// ── Status dots ──────────────────────────────────────────────────────────

export const DOT = {
  /** Small dot (1.5 × 1.5). */
  sm:    'w-1.5 h-1.5 rounded-full shrink-0',
  /** Medium dot (2 × 2). */
  md:    'w-2 h-2 rounded-full',
  /** Vertical bar indicator (0.5 × 4). */
  bar:   'w-0.5 h-4 rounded-full shrink-0',

  // ── Dot colors ──
  success:  'bg-emerald-500',
  warning:  'bg-amber-500',
  error:    'bg-red-500',
  info:     'bg-sky-500',
  violet:   'bg-violet-500',
  cyan:     'bg-cyan-500',
  orange:   'bg-orange-500',
  neutral:  'bg-white/10',

  /** Pulse animation modifier. */
  pulse: 'animate-pulse',
} as const;

// ── Text ─────────────────────────────────────────────────────────────────

export const TEXT = {
  /** Primary (titles, main content). */
  primary:       'text-sm text-neutral-200',
  /** Primary bold (card titles). */
  primaryBold:   'text-sm text-neutral-200 font-medium',
  /** Secondary (labels, descriptions). */
  secondary:     'text-xs text-neutral-400',
  /** Muted (scope keys, meta). */
  muted:         'text-xs text-neutral-500',
  /** Tertiary (sub-labels, tracking). */
  tertiary:      'text-[10px] text-neutral-500',
  /** Dim (timestamps, least-priority). */
  dim:           'text-[10px] text-neutral-600',
  /** Monospace (scope keys, IDs, code). */
  mono:          'text-xs font-mono text-neutral-300',
  /** Muted monospace (secondary IDs). */
  monoMuted:     'text-xs font-mono text-neutral-500',
  /** Section label (uppercase dividers). */
  sectionLabel:  'text-[10px] text-neutral-500 uppercase tracking-wider',
  /** Empty state message. */
  emptyState:    'text-xs text-neutral-600 italic',
} as const;

// ── Skeleton / Placeholder ───────────────────────────────────────────────

export const SKELETON = {
  /** Skeleton bar (set width via utility: w-3/4, w-1/2, etc.). */
  bar: 'bg-white/[0.04] rounded animate-pulse',
} as const;

// ── Spinner ──────────────────────────────────────────────────────────────

export const SPINNER = {
  /** Small spinner (2.5 × 2.5). */
  sm: 'h-2.5 w-2.5 rounded-full border-2 border-amber-400/40 border-t-amber-400 animate-spin',
  /** Medium spinner (3 × 3). */
  md: 'h-3 w-3 rounded-full border-2 border-amber-400/40 border-t-amber-400 animate-spin',
} as const;
